import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PerformanceTab = ({ performanceData, canViewDetails = false }) => {
  const [selectedReview, setSelectedReview] = useState(null);

  const getRatingColor = (rating) => {
    if (rating >= 4.5) return 'text-success bg-success/10';
    if (rating >= 3.5) return 'text-primary bg-primary/10';
    if (rating >= 2.5) return 'text-warning bg-warning/10';
    return 'text-error bg-error/10';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-success bg-success/10';
      case 'in_progress':
        return 'text-warning bg-warning/10';
      case 'pending':
        return 'text-muted-foreground bg-muted';
      case 'overdue':
        return 'text-error bg-error/10';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars?.push(
        <Icon key={i} name="Star" size={16} color="var(--color-warning)" className="fill-current" />
      );
    }

    if (hasHalfStar) {
      stars?.push(
        <Icon key="half" name="Star" size={16} color="var(--color-warning)" className="fill-current opacity-50" />
      );
    }

    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars?.push(
        <Icon key={`empty-${i}`} name="Star" size={16} color="var(--color-muted-foreground)" />
      );
    }

    return stars;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">Performance Overview</h3>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Icon name="TrendingUp" size={16} />
          <span>Last updated: {performanceData?.lastUpdated}</span>
        </div>
      </div>
      {/* Performance Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
              <Icon name="Target" size={20} color="var(--color-primary)" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Overall Rating</p>
              <div className="flex items-center gap-2">
                <span className="text-xl font-bold text-foreground">{performanceData?.overallRating}</span>
                <div className="flex items-center gap-1">
                  {renderStars(performanceData?.overallRating)}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-success/10 rounded-full flex items-center justify-center">
              <Icon name="CheckCircle" size={20} color="var(--color-success)" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Goals Completed</p>
              <div className="flex items-center gap-2">
                <span className="text-xl font-bold text-foreground">{performanceData?.goalsCompleted}</span>
                <span className="text-sm text-muted-foreground">/ {performanceData?.totalGoals}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-accent/10 rounded-full flex items-center justify-center">
              <Icon name="Calendar" size={20} color="var(--color-accent)" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Next Review</p>
              <p className="text-lg font-semibold text-foreground">{performanceData?.nextReview}</p>
            </div>
          </div>
        </div>
      </div>
      {/* Current Goals */}
      <div className="bg-card rounded-lg border border-border p-4">
        <h4 className="font-semibold text-foreground mb-4">Current Goals & Objectives</h4>
        <div className="space-y-4">
          {performanceData?.currentGoals?.map((goal) => (
            <div key={goal?.id} className="bg-muted/30 rounded-lg p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h5 className="font-medium text-foreground mb-1">{goal?.title}</h5>
                  <p className="text-sm text-muted-foreground">{goal?.description}</p>
                </div>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(goal?.status)}`}>
                  {goal?.status?.replace('_', ' ')?.toUpperCase()}
                </span>
              </div>
              
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Progress</span>
                <span className="text-sm font-medium text-foreground">{goal?.progress}%</span>
              </div>
              
              <div className="w-full bg-muted rounded-full h-2 mb-3">
                <div 
                  className="bg-primary h-2 rounded-full transition-all duration-300"
                  style={{ width: `${goal?.progress}%` }}
                ></div>
              </div>
              
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Icon name="Calendar" size={14} />
                  <span>Due: {goal?.dueDate}</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Icon name="User" size={14} />
                  <span>Assigned by: {goal?.assignedBy}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Performance Reviews */}
      <div className="bg-card rounded-lg border border-border p-4">
        <h4 className="font-semibold text-foreground mb-4">Performance Reviews</h4>
        <div className="space-y-4">
          {performanceData?.reviews?.map((review) => (
            <div key={review?.id} className="border border-border rounded-lg p-4">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h5 className="font-medium text-foreground">{review?.period}</h5>
                  <p className="text-sm text-muted-foreground">Reviewed by: {review?.reviewer}</p>
                </div>
                <div className="text-right">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg font-bold text-foreground">{review?.rating}</span>
                    <div className="flex items-center gap-1">
                      {renderStars(review?.rating)}
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">{review?.date}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                {review?.categories?.map((category) => (
                  <div key={category?.name} className="text-center">
                    <p className="text-sm text-muted-foreground mb-1">{category?.name}</p>
                    <div className="flex items-center justify-center gap-1">
                      {renderStars(category?.rating)}
                    </div>
                    <p className="text-sm font-medium text-foreground mt-1">{category?.rating}</p>
                  </div>
                ))}
              </div>
              
              {canViewDetails && (
                <div className="pt-3 border-t border-border">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedReview(review)}
                  >
                    <Icon name="Eye" size={16} className="mr-2" />
                    View Details
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      {/* Skills Assessment */}
      <div className="bg-card rounded-lg border border-border p-4">
        <h4 className="font-semibold text-foreground mb-4">Skills Assessment</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {performanceData?.skills?.map((skill) => (
            <div key={skill?.name} className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-foreground">{skill?.name}</span>
                <span className="text-sm text-muted-foreground">{skill?.level}/5</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div 
                  className="bg-primary h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(skill?.level / 5) * 100}%` }}
                ></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PerformanceTab;